import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getWhatsAppStatus, 
  getWhatsAppQR, 
  restartWhatsAppService, 
  sendTestNotification 
} from '../api/client';

const TelegramSetup = () => {
  const { user, isAdmin } = useAuth();
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState('');
  const isKontrol = !window.location.hostname.includes('farmavida.site') || window.location.hostname.includes('.kontrol.');
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [setupInstructions, setSetupInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const checkTelegramStatus = async () => {
    try {
      setError(''); // Clear previous errors
      
      // Debug: Check if user is properly authenticated
      console.log('Telegram Status Check - User:', user);
      console.log('Telegram Status Check - Is Admin:', isAdmin);
      console.log('Telegram Status Check - Token:', localStorage.getItem('farmavida_token') ? 'Present' : 'Missing');
      
      const data = await getWhatsAppStatus(); // Keep same API endpoint name for compatibility
      setTelegramStatus(data);
      
      if (!data.connected) {
        try {
          const setupData = await getWhatsAppQR(); // Keep same API endpoint name for compatibility
          setSetupInstructions(setupData.setup_instructions);
        } catch (setupErr) {
          console.warn('Could not get setup instructions:', setupErr);
          setSetupInstructions('');
        }
      } else {
        setSetupInstructions('');
      }
      
    } catch (err) {
      console.error('Error checking Telegram status:', err);
      console.error('Error response:', err.response);
      
      // Better error handling for authentication issues
      if (err.response?.status === 403) {
        setError('No tienes permisos para acceder al servicio de Telegram. Solo administradores pueden configurar Telegram.');
      } else if (err.response?.status === 401) {
        setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        setError(`Error conectando con el servicio de Telegram: ${err.response?.data?.detail || err.message}`);
      }
      
      setTelegramStatus(null);
      setSetupInstructions('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setError('Acceso denegado. Solo los administradores pueden gestionar la configuración de Telegram.');
      setLoading(false);
      return;
    }
    
    checkTelegramStatus();
    // Check status every 10 seconds
    const interval = setInterval(checkTelegramStatus, 10000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const restartTelegramServiceHandler = async () => {
    try {
      setError('');
      const result = await restartWhatsAppService(); // Keep same API endpoint name for compatibility
      alert('✅ Servicio de Telegram reiniciado correctamente.');
      setLoading(true);
      setTimeout(checkTelegramStatus, 5000);
    } catch (err) {
      setError(`Error reiniciando el servicio: ${err.response?.data?.detail || err.message}`);
    }
  };

  const saveTelegramConfig = async () => {
    if (!botToken.trim() || !chatId.trim()) {
      setConfigMsg('❌ Completa el Bot Token y el Chat ID');
      return;
    }
    setConfigSaving(true);
    try {
const token = localStorage.getItem('farmavida_token');
      const res = await fetch('/api/telegram/configure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ bot_token: botToken, chat_id: chatId })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Error'); }
      setConfigMsg('✅ Configuración guardada. El sistema se reiniciará en unos segundos...');
      setBotToken('');
      setChatId('');
      setTimeout(checkTelegramStatus, 6000);
    } catch (err) {
      setConfigMsg('❌ Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setConfigSaving(false);
    }
  };

  const testNotification = async () => {
    try {
      const result = await sendTestNotification(); // Keep same API endpoint name for compatibility
      if (result.success) {
        alert('✅ Notificación de prueba enviada! Revisa tu Telegram.');
      } else {
        alert(`❌ Error enviando notificación: ${result.error || 'Error desconocido'}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err.response?.data?.detail || err.message}`);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="text-red-600 text-4xl mr-4">🚫</div>
              <div>
                <h1 className="text-2xl font-bold text-red-800 mb-2">Acceso Restringido</h1>
                <p className="text-red-600">
                  Solo los administradores pueden acceder a la configuración de Telegram.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-5xl">📱</div>
              <div>
                <h1 className="text-3xl font-bold text-blue-900">Configuración de Telegram</h1>
                <p className="text-gray-600 mt-1">
                  Configuración del bot de Telegram para notificaciones de FARMAVIDA
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={checkTelegramStatus}
                className="btn-outline"
                disabled={loading}
              >
                🔄 Actualizar Estado
              </button>
            </div>
          </div>
        </div>

        {/* Configuración del Bot - Solo Kontrol */}
        {isKontrol && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-6 border-l-4 border-indigo-500">
            <h2 className="text-xl font-bold text-gray-800 mb-2">🤖 Configurar tu Bot de Telegram</h2>
            <p className="text-gray-500 text-sm mb-4">Ingresa las credenciales de tu bot para recibir notificaciones en tu propio Telegram.</p>
            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bot Token</label>
                <input type="text" value={botToken} onChange={e => setBotToken(e.target.value)}
                  placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chat ID</label>
                <input type="text" value={chatId} onChange={e => setChatId(e.target.value)}
                  placeholder="-1001234567890"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>
            {configMsg && <p className="text-sm mb-3">{configMsg}</p>}
            <button onClick={saveTelegramConfig} disabled={configSaving}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
              {configSaving ? 'Guardando...' : '💾 Guardar configuración'}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-600 text-xl mr-3">⚠️</div>
              <div>
                <h3 className="font-medium text-red-800">Error de Conexión</h3>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estado del Servicio</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
              <span className="ml-2 text-gray-600">Verificando estado...</span>
            </div>
          ) : telegramStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${telegramStatus.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div>
                  <span className="text-sm text-gray-500">Estado</span>
                  <p className="font-semibold">
                    {telegramStatus.connected ? '✅ Conectado' : '❌ Desconectado'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-blue-500 text-xl">📱</div>
                <div>
                  <span className="text-sm text-gray-500">Plataforma</span>
                  <p className="font-semibold">{telegramStatus.platform || 'Telegram'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-green-500 text-xl">🤖</div>
                <div>
                  <span className="text-sm text-gray-500">Bot</span>
                  <p className="font-semibold">{telegramStatus.name || 'FARMAVIDA Bot'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-purple-500 text-xl">🔄</div>
                <div>
                  <span className="text-sm text-gray-500">Reconexiones</span>
                  <p className="font-semibold">
                    {telegramStatus.reconnectCount || 0}/{telegramStatus.maxReconnects || 5}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">❌</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No se pudo obtener el estado
              </h3>
              <p className="text-gray-600">
                Error al conectar con el servicio de Telegram
              </p>
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        {setupInstructions && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              📋 Instrucciones de Configuración
            </h3>
            <div className="whitespace-pre-line text-blue-800 text-sm">
              {setupInstructions}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Acciones</h2>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={testNotification}
              className="btn-primary"
              disabled={loading}
            >
              📤 Enviar Notificación de Prueba
            </button>
            
            <button
              onClick={restartTelegramServiceHandler}
              className="btn-outline"
              disabled={loading}
            >
              🔄 Reiniciar Servicio
            </button>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">ℹ️ Información</h4>
            <p className="text-sm text-gray-600">
              Las notificaciones de Telegram se enviarán automáticamente cuando se registren:
            </p>
            <ul className="text-sm text-gray-600 mt-2 ml-4">
              <li>• 💰 Nuevas ventas</li>
              <li>• 💸 Gastos registrados</li>
              <li>• 💳 Pagos a proveedores</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramSetup;